import { useCallback, useEffect, useRef, useState } from 'react'

import { useLocation, useNavigate, useSearchParams } from 'react-router'

import {
  CSV_IMPORT_RETURN_DELAY_MS,
  CSV_IMPORT_RETURN_TO_PARAM,
  type CsvImportAppliedCounts,
  type CsvImportKind,
  type CsvImportReturnState,
  sanitizeCsvImportReturnTo,
  withCsvImportReturnTo,
} from '~/models/csvImportReturn'
import { routePaths } from '~/routes/routePaths'

/**
 * returnTo が無い時 (CSV 入出力の一覧から開いた時) の行き先 = その種別のデータが見える業務画面
 * (spec admin/data/uploads/common.md「業務画面との行き来」の表)。 その業務画面がまだ見えていない
 * 種別 (供給地点マスタ = 顧客情報の契約推移・需要グループ) は CSV 入出力の一覧 (AC-DUC-058)。
 * 業務画面を実装したら、 ここをその画面に替える。
 */
const CSV_IMPORT_HOME_PATHS: Record<CsvImportKind, string> = {
  supply48Data: routePaths.supply48Data.path,
  bilateralTrade: routePaths.supplyBilateral.path,
  cisContract: routePaths.adminUploads.path,
  demandGroups: routePaths.adminUploads.path,
  customerContract: routePaths.adminUploads.path,
}

type UseCsvImportReturnResult = {
  /** キャンセルの行き先。 returnTo があればそこ、 無ければ CSV 入出力の一覧 */
  cancelPath: string
  /** 業務画面へ戻るのを待っている間 true。 確定を重ねて押せないようにする */
  isReturning: boolean
  /** 取り込みが反映された時に呼ぶ。 1.5 秒後に移る (件数は業務画面の Alert に渡す) */
  returnAfterApply: (counts?: CsvImportAppliedCounts) => void
}

/** 取り込み画面が使う。 returnTo を読み、 キャンセルと取り込み後の戻り先を決める */
export function useCsvImportReturn(
  kind: CsvImportKind,
): UseCsvImportReturnResult {
  const [searchParams] = useSearchParams()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const returnTo = sanitizeCsvImportReturnTo(
    searchParams.get(CSV_IMPORT_RETURN_TO_PARAM),
    pathname,
  )
  const destination = returnTo ?? CSV_IMPORT_HOME_PATHS[kind]
  const [isReturning, setIsReturning] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  // 待っている間に利用者が別の画面へ移ったら、 後から引き戻さない
  useEffect(() => () => clearTimeout(timerRef.current), [])

  const returnAfterApply = useCallback(
    (counts?: CsvImportAppliedCounts): void => {
      const state: CsvImportReturnState | undefined =
        counts === undefined
          ? undefined
          : { csvImportApplied: { kind, counts } }
      setIsReturning(true)
      clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        void navigate(destination, { state })
      }, CSV_IMPORT_RETURN_DELAY_MS)
    },
    [destination, kind, navigate],
  )

  return {
    cancelPath: returnTo ?? routePaths.adminUploads.path,
    isReturning,
    returnAfterApply,
  }
}

/**
 * 業務画面の CSV アップロード導線の行き先を作る関数。 今の画面 (path + search) を戻り先として付ける。
 * 行き先が描画ごとに変わる (入札のブロック種別ごと等) 時に使う
 */
export function useCsvImportLinkBuilder(): (importPath: string) => string {
  const { pathname, search } = useLocation()
  return (importPath) =>
    withCsvImportReturnTo(importPath, `${pathname}${search}`)
}

/** 業務画面の CSV アップロード導線の行き先。 今の画面 (path + search) を戻り先として付ける */
export function useCsvImportLink(importPath: string): string {
  return useCsvImportLinkBuilder()(importPath)
}
